import { useCallback, useEffect, useRef, useState } from 'react'

import { useDispatch } from 'react-redux'

import type { BasketUpsellProductActionSource } from '@features/scrollable-basket'

import { actionTypes } from 'actions/actionTypes'

type ProductAction = (productId: string, source: BasketUpsellProductActionSource) => void

type PendingOperation = {
  errorAtStart: unknown
  hasStarted: boolean
  productId: string
  type: 'add' | 'remove'
}

type ProductError = {
  message: string
  title: string
}

type UseBasketProductErrorHandlingInput = {
  addProduct: ProductAction
  basketError?: unknown
  isBasketValidating?: boolean
  removeProduct: ProductAction
}

const ADD_PRODUCT_ERROR: ProductError = {
  title: "Couldn't add item",
  message: "It's been removed from your basket. Please try again.",
}

const REMOVE_PRODUCT_ERROR: ProductError = {
  title: "Couldn't remove item",
  message: "It's back in your basket. Please try again.",
}

export const useBasketProductErrorHandling = ({
  addProduct,
  basketError,
  isBasketValidating = false,
  removeProduct,
}: UseBasketProductErrorHandlingInput) => {
  const dispatch = useDispatch()
  const pendingOperation = useRef<PendingOperation | null>(null)
  const hasRecoveryValidationStarted = useRef(false)
  const [pendingProductIds, setPendingProductIds] = useState<ReadonlySet<string>>(new Set())
  const [productError, setProductError] = useState<ProductError | null>(null)

  const startOperation = useCallback(
    (productId: string, type: PendingOperation['type']) => {
      pendingOperation.current = {
        errorAtStart: basketError,
        hasStarted: isBasketValidating,
        productId,
        type,
      }
      setPendingProductIds(new Set([productId]))
      setProductError(null)
    },
    [basketError, isBasketValidating],
  )

  const addProductWithErrorHandling = useCallback<ProductAction>(
    (productId, source) => {
      startOperation(productId, 'add')
      addProduct(productId, source)
    },
    [addProduct, startOperation],
  )

  const removeProductWithErrorHandling = useCallback<ProductAction>(
    (productId, source) => {
      startOperation(productId, 'remove')
      removeProduct(productId, source)
    },
    [removeProduct, startOperation],
  )

  useEffect(() => {
    const operation = pendingOperation.current

    if (!operation) return

    if (isBasketValidating) {
      operation.hasStarted = true
    }

    if (basketError && basketError !== operation.errorAtStart) {
      dispatch({
        type:
          operation.type === 'add'
            ? actionTypes.BASKET_PRODUCT_REMOVE
            : actionTypes.BASKET_PRODUCT_ADD,
        productId: operation.productId,
      })
      setProductError(operation.type === 'add' ? ADD_PRODUCT_ERROR : REMOVE_PRODUCT_ERROR)
      pendingOperation.current = null
      setPendingProductIds(new Set())

      return
    }

    if (operation.hasStarted && !isBasketValidating) {
      pendingOperation.current = null
      setPendingProductIds(new Set())
    }
  }, [basketError, dispatch, isBasketValidating])

  useEffect(() => {
    if (!productError) {
      hasRecoveryValidationStarted.current = false

      return
    }

    if (isBasketValidating) {
      hasRecoveryValidationStarted.current = true

      return
    }

    if (hasRecoveryValidationStarted.current && !basketError) {
      hasRecoveryValidationStarted.current = false
      setProductError(null)
    }
  }, [basketError, isBasketValidating, productError])

  return {
    addProduct: addProductWithErrorHandling,
    dismissProductError: () => setProductError(null),
    pendingProductIds,
    productError,
    removeProduct: removeProductWithErrorHandling,
  }
}
